import React, { Component } from "react";
import { Button, Col, Form, Modal, Row, Select, Spin, Tooltip } from "antd";

import moment from "moment";

import QueueAnim from "rc-queue-anim";
import { connect } from "react-redux";

import { afdFileService, equipmentsService } from "../../redux/services";
import { DatePickerWithMask, UIFormSection, UIInfoBanner, UIPageListing, UIUpload } from "../../components";
import { API_ERRO_TYPE_CANCEL } from "../../config/general";
import { formatTzDate } from "../../utils/formatTzDate";

const config = {
	title     : "Importar Arquivo AFD",
	subtitle  : "Importe arquivos AFD para atualizar os registros de ponto e manter as informações em dia.",
	icon      : <i className="fal fa-file-import" />,
	infoBanner: {
		icon       : <i className="fal fa-info-circle" />,
		title      : "Sobre as importações",
		description: "Os arquivos são processados em segundo plano. Enquanto houver importações em andamento, o status é atualizado automaticamente a cada 10 segundos.",
	},
};

const FINISHED_RECORD_STATUS = ["failed", "success", "successed"];

const STATUS = {
	em_analise           : { tag: "neutral", kpiTone: "neutral", icon: "fa-search", caption: "Em análise" },
	aguardando_importacao: { tag: "warning", kpiTone: "amber", icon: "fa-clock", caption: "Aguardando processamento" },
	importando           : { tag: "info", kpiTone: "blue", icon: "fa-sync", caption: "Em processamento" },
	importado            : { tag: "success", kpiTone: "green", icon: "fa-check-circle", caption: "Concluído com sucesso" },
	erro                 : { tag: "error", kpiTone: "red", icon: "fa-times-circle", caption: "Falha na importação" },
	cancelado            : { tag: "error", kpiTone: "red", icon: "fa-ban", caption: "Importação cancelada" },
};

const importedRecords = ({ arquivos }) => (arquivos || []).reduce((acc, item) => acc + (item?.afd_records || []).reduce((acc2, record) => acc2 + (FINISHED_RECORD_STATUS.includes(record?.status) ? (record?.subtotal || 0) : 0), 0), 0);

const totalRecords = ({ arquivos }) => (arquivos || []).reduce((acc, item) => acc + (item?.total_linhas || 0), 0);

// O arquivo pode estar "importado" enquanto as partes ainda estão sendo processadas
const resolveStatus = ({ status, status_nome, arquivos }) => (
	status !== "importado" || (arquivos || []).every(item => item.status_nome === "Importado")
		? { key: status, title: status_nome }
		: { key: "importando", title: "Importando" }
);

const renderStatusTag = (status) => (
	<span className={`status-tag status-tag-${(STATUS[status.key] || STATUS.em_analise).tag}`}>
		<span className="status-tag-dot" />
		{status.title}
	</span>
);

class Index extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
			isSending: false,
			data: [],
			kpis: null,
			pagination: {
				current: 1,
				pageSize: 20,
				total: 0,
			},
			fetchInterval: null,
			equipmentsIsLoading: false,
			equipments: [],
			equipamento_id: null,
		};
	}

	componentDidMount() {
		this.fetchEquipments();
		this.fetchGetAll();
	}

	componentWillUnmount() {
		this.clearIntervalFetch();
	}

	fetchGetAll = (isInterval = false) => {
		const { fetchInterval, pagination } = this.state;

		this.setState({
			isLoading: !isInterval,
		});

		afdFileService
			.getAll({
				page: pagination.current,
				limit: pagination.pageSize,
			})
			.then((response) => {
				const { data, meta, kpis } = response.data || {};
				this.setState((state) => ({
					isLoading: isInterval,
					data: data || [],
					kpis: kpis ?? null,
					pagination: {
						...state.pagination,
						current: meta?.current_page ?? state.pagination.current,
						total: meta?.total ?? 0,
					},
				}));

				const hasFetch = data.some((item) => {
					// Verifica o status geral do arquivo
					if (["importando", "aguardando_importacao"].includes(item.status)) {
						return true;
					}

					if (
						item.arquivos &&
						item.arquivos.some((arquivo) =>
							arquivo.afd_records?.some(
								(record) => !['failed', 'success', 'successed'].includes(record.status)
							)
						)
					) {
						return true;
					}

					return false;
				});

				if (hasFetch) {
					if (!fetchInterval) {
						this.initTimeoutFetch();
					}
				} else {
					this.clearIntervalFetch();
				}
			})
			.catch((data) => {
				this.setState({
					isLoading: isInterval,
				});
				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	fetchEquipments = () => {
    this.setState({
      equipmentsIsLoading: true,
    })

    equipmentsService
      .getAll({
        is_active: 1,
				limit: 100,
				incluir_app: 1,
      })
      .then((response) => {
        this.setState({
          equipmentsIsLoading: false,
          equipments: response?.data?.data || [],
        })
      })
      .catch((data) => {
        if (data.error_type === API_ERRO_TYPE_CANCEL) return null

        this.setState({
          equipmentsIsLoading: false,
        })

        Modal.error({
          title: 'Ocorreu um erro!',
          content: String(data),
        })
      })
  }

	resetFields = () => {
		this.setState({});
	};

	clearIntervalFetch = async () => {
		if (this.state.fetchInterval) {
			clearInterval(this.state.fetchInterval);
			this.setState({ fetchInterval: null });
		}
	}

	initTimeoutFetch = () => {
		const interval = setInterval(() => {
			this.fetchGetAll();
		}, 10000);
		this.setState({ fetchInterval: interval });
	}

	onPaginationChange = (page, pageSize) => {
		this.setState((state) => ({
			pagination: {
				...state.pagination,
				current: page,
				pageSize,
			},
		}), () => {
			this.fetchGetAll();
		});
	};

	onFinish = (values) => {
		const file = this.upload.getFiles();

		if (file.hasError) {
			Modal.error({
				title: "Ocorreu um erro!",
				content: file.error,
			});

			return false;
		}

		if (!file.files.length) {
			Modal.error({
				title: "Aviso!",
				content: "Preencha o campo Arquivo (txt)",
			});

			return false;
		}

		this.setState({
			isSending: true,
		});

		const data = {
			...values,
			tipo: 'manual',
		};
		data.data_inicial = data.data_inicial ? moment(data.data_inicial).format('YYYY-MM-DD') : null;

		// File
		if (file.files.length) {
			if (!file.files[0].uuid) {
				data.afd = file.files[0];
			}
		} else {
			data.afd = null;
		}

		afdFileService.upload(data)
			.then((response) => {
				this.setState((state) => ({
					isSending: false,
					pagination: {
						...state.pagination,
						current: 1,
					},
				}), () => {
					Modal.success({
						content: "Ação realizada com sucesso!",
						okText: "Ok",
					});

					if (this.upload) this.upload.reset();

					this.fetchGetAll();
				});
			})
			.catch((data) => {
				this.setState({
					isSending: false,
				});
				Modal.error({
					title: "Ocorreu um erro!",
					content: String(data),
				});
			});
	};

	onEquipamentoChange = (equipamento_id) => {
		this.setState({ equipamento_id });
	};

	kpis = () => {
		const lastImport = this.state.kpis?.ultima_importacao;

		if (!lastImport) {
			return [
				{ key: "lastImport", tone: "blue", icon: <i className="fal fa-cloud-upload" />, title: "Última importação", value: "—", caption: "Nenhum arquivo importado" },
				{ key: "records", tone: "green", icon: <i className="fal fa-file-alt" />, title: "Total de registros importados", value: "—", caption: "No último arquivo" },
				{ key: "period", tone: "violet", icon: <i className="fal fa-calendar-alt" />, title: "Data inicial", value: "—", caption: "Na última importação" },
				{ key: "status", tone: "neutral", icon: <i className="fal fa-check-circle" />, title: "Status da última importação", value: "—" },
			];
		}

		const status = resolveStatus(lastImport);
		const statusConfig = STATUS[status.key] || STATUS.em_analise;
		const isWaiting = lastImport.status === "aguardando_importacao";

		return [
			{
				key    : "lastImport",
				tone   : "blue",
				icon   : <i className="fal fa-cloud-upload" />,
				title  : "Última importação",
				value  : formatTzDate(lastImport.data_importacao, "-03:00"),
				caption: (
					<Tooltip title={lastImport.arquivo_nome ?? lastImport.equipamento?.descricao}>
						<span className="kpi-card-caption-ellipsis">{lastImport.arquivo_nome ?? lastImport.equipamento?.descricao}</span>
					</Tooltip>
				),
			},
			{
				key    : "records",
				tone   : "green",
				icon   : <i className="fal fa-file-alt" />,
				title  : "Total de registros importados",
				value  : isWaiting ? "—" : importedRecords(lastImport),
				caption: isWaiting ? "No último arquivo" : `De ${totalRecords(lastImport)} registros no último arquivo`,
			},
			{
				key    : "period",
				tone   : "violet",
				icon   : <i className="fal fa-calendar-alt" />,
				title  : "Data inicial",
				value  : lastImport.data_inicial ? moment(lastImport.data_inicial).format("DD/MM/YYYY") : "—",
				caption: "Na última importação",
			},
			{
				key    : "status",
				tone   : statusConfig.kpiTone,
				icon   : <i className={`fal ${statusConfig.icon}`} />,
				title  : "Status da última importação",
				value  : renderStatusTag(status),
				caption: statusConfig.caption,
			},
		];
	};

	columns = () => [
		{
			title : "Data da Importação",
			render: ({ data_importacao }) => formatTzDate(data_importacao, "-03:00") ?? "-",
		},
		{
			title : "Descrição",
			render: ({ equipamento }) => equipamento?.descricao ?? "-",
		},
		{
			title : "Número de série",
			render: ({ numero_serial_rep }) => numero_serial_rep ?? "-",
		},
		{
			title : "Nome do arquivo",
			render: ({ arquivo_nome }) => arquivo_nome ?? "-",
		},
		{
			title : "A partir de",
			render: ({ data_inicial }) => data_inicial ? moment(data_inicial).format("DD/MM/YYYY") : "-",
		},
		{
			title : "Total de Registros",
			render: (item) => item.status !== "aguardando_importacao" ? `${importedRecords(item)} / ${totalRecords(item)}` : "--",
		},
		{
			title : "Status",
			render: (item) => {
				const status = resolveStatus(item);

				return renderStatusTag(status);
			},
		},
	];

	renderImportForm = () => {
		const { isSending, equipments, equipamento_id, equipmentsIsLoading, fetchInterval } = this.state;

		return (
			<Form
				ref={(el) => (this.form = el)}
				layout="vertical"
				scrollToFirstError
				onFinish={this.onFinish}
				disabled={fetchInterval}
				initialValues={{}}
				className="afd-import-form"
			>
				<UIFormSection
					icon={<i className="fal fa-file-upload" />}
					title="Importar novo arquivo"
					description="Selecione o equipamento, a data a partir da qual os registros serão aplicados e o arquivo AFD."
					extra={
						<Button
							type="primary"
							htmlType="submit"
							icon={<i className="far fa-file-import" />}
							loading={isSending}
							disabled={!equipamento_id || isSending}
						>
							{isSending ? "Importando" : "Importar"}
						</Button>
					}
				>
					<Row gutter={24}>
						<Col xs={24} xl={16}>
							<Row gutter={16}>
								<Col xs={24} md={16}>
									<label className="form-label required">Equipamento:</label>
									<Form.Item
										name="equipamento_id"
									>
										<Select
											optionFilterProp="children"
											filterOption={(input, option) => {
												const data = ([].concat(option.children ?? []).join(' ') || '').toLowerCase();
												return data.indexOf(input.toLowerCase()) >= 0;
											}}
											allowClear
											placeholder="Selecione o equipamento"
											notFoundContent={
												equipmentsIsLoading ? (
													<Spin
														indicator={
															<i className="fad fa-spinner-third fa-spin" />
														}
													/>
												) : null
											}
											onChange={this.onEquipamentoChange}
											showSearch
											style={{ width: '100%' }}
										>
											{equipments.map((item, index) => (
												<Select.Option key={index} value={item.id}>
													{item.modelo === 'App'
														? 'App (Importar de terceiros)'
														: `${item.descricao} (${item.numero_serial_rep} - ${(item.configuracao?.tipo_documento || 'S/I').toUpperCase()})`}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
								</Col>

								<Col xs={24} md={8}>
									<DatePickerWithMask
										label="A partir da data"
										name="data_inicial"
										required={true}
										disabled={!equipamento_id}
									/>
								</Col>

								<Col xs={24}>
									<UIUpload
										ref={(el) => (this.upload = el)}
										label="Arquivo (txt):"
										labelError="Arquivo (txt):"
										acceptedFiles={["txt"]}
										inputDisabled={!equipamento_id}
									/>
								</Col>
							</Row>
						</Col>

						<Col xs={24} xl={8}>
							<UIInfoBanner
								icon={<i className="fal fa-exclamation-circle" />}
								title="Importante"
								description="Os registros do arquivo serão aplicados somente a partir da data informada. Verifique se o arquivo corresponde ao equipamento selecionado antes de importar."
							/>
						</Col>
					</Row>
				</UIFormSection>
			</Form>
		);
	};

	render() {
		const { isLoading, data, pagination } = this.state;

		return (
			<QueueAnim className="site-content-inner page-import-afd-file alternative-wrapper">
				<div className="page-content fixed-header" key="1">
					<UIPageListing
						icon={config.icon}
						title={config.title}
						subtitle={config.subtitle}
						kpis={this.kpis()}
						afterKpis={this.renderImportForm()}
						stickyToolbarOnly
						infoBanner={config.infoBanner}
						appendSearch={<p className="form-section-title afd-history-title">Histórico de importações</p>}
						isLoading={isLoading}
						listType="list"
						data={data}
						enableSearch={false}
						showListTypeChange={false}
						showFilters={false}
						pagination={pagination}
						columns={this.columns()}
						onPaginationChange={this.onPaginationChange}
						emptyTitle="Nenhuma importação encontrada"
						emptyDescription="Os arquivos AFD importados aparecerão aqui."
					/>
				</div>
			</QueueAnim>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	return {
		permissions: state.auth.userData.permissions,
	};
};

export default connect(mapStateToProps)(Index);
